import { useApp } from '../context/AppContext'
import { useTheme } from '../hooks/useTheme'
import { ROLE_CONFIG, SUBJECTS, getGradeInfo } from '../data/index.js'

// 作业卡片组件
export default function HomeworkCard({ homework, showActions = true }) {
  const { navigate, userRole, USER_ROLES } = useApp()
  const { bgWhite, textPrimary, textSecondary, textMuted, shadowColor, borderColor } = useTheme()

  const subject = SUBJECTS.find(s => s.id === homework.subject) || SUBJECTS[0]
  const isTeacher = userRole === USER_ROLES.TEACHER
  const isStudent = userRole === USER_ROLES.STUDENT

  // 计算截止日期状态
  const deadlineDate = new Date(homework.deadline)
  const now = new Date()
  const isExpired = deadlineDate < now
  const daysLeft = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24))

  // 后端返回的字段适配
  const mySubmission = homework.mySubmission || null
  const submissionCount = homework.submission_count || homework.submissions?.length || 0
  const gradedCount = homework.graded_count || homework.submissions?.filter(s => s.grade)?.length || 0

  return (
    <button
      className="rounded-2xl p-4 text-left border-none cursor-pointer transition-all active:scale-98 w-full"
      style={{ background: bgWhite, boxShadow: shadowColor }}
      onClick={() => navigate('homework', homework.id)}
    >
      {/* 科目标签 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span
            className="px-2.5 py-1 rounded-lg text-xs font-bold"
            style={{ background: subject.color + '15', color: subject.color }}
          >
            <i className={`fa-solid ${subject.icon} mr-1`} />
            {subject.name}
          </span>

          {isExpired && (
            <span className="px-2 py-0.5 rounded text-[10px] font-bold" style={{ background: '#FFF0F0', color: '#FF6B6B' }}>
              已截止
            </span>
          )}
        </div>

        {!isExpired && isStudent && (
          <span className="text-[11px]" style={{ color: daysLeft <= 1 ? '#FF6B6B' : textSecondary }}>
            {daysLeft <= 0 ? '今天截止' : `剩${daysLeft}天`}
          </span>
        )}
      </div>

      {/* 作业标题 */}
      <h4 className="text-[15px] font-bold mb-2" style={{ color: textPrimary }}>
        {homework.title}
      </h4>

      {/* 老师名称 */}
      <p className="text-[12px] mb-3" style={{ color: textSecondary }}>
        <i className="fa-solid fa-user-tie mr-1" />
        {homework.teacher_name} · {new Date(homework.created_at).toLocaleDateString('zh-CN')}
      </p>

      {/* 附件提示 */}
      {homework.attachments && homework.attachments.length > 0 && (
        <div className="flex items-center gap-1.5 mb-3">
          <i className="fa-solid fa-paperclip text-xs" style={{ color: '#6C63FF' }} />
          <span className="text-[11px]" style={{ color: '#6C63FF' }}>
            {homework.attachments.length}个附件
          </span>
        </div>
      )}

      {/* 状态信息 */}
      <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid ' + borderColor }}>
        {isStudent ? (
          mySubmission ? (
            <div className="flex items-center gap-2">
              <span className="text-[12px]" style={{ color: '#20BF6B' }}>
                <i className="fa-solid fa-check-circle mr-1" />
                已提交
              </span>
              {mySubmission.grade && (
                <span
                  className="px-2 py-0.5 rounded text-xs font-bold"
                  style={{ background: getGradeInfo(mySubmission.grade).bg, color: getGradeInfo(mySubmission.grade).color }}
                >
                  {getGradeInfo(mySubmission.grade).grade}级
                </span>
              )}
            </div>
          ) : (
            <span className="text-[12px]" style={{ color: isExpired ? '#FF6B6B' : textSecondary }}>
              {isExpired ? '逾期未提交' : '待提交'}
            </span>
          )
        ) : isTeacher ? (
          <div className="flex items-center gap-3">
            <span className="text-[12px]" style={{ color: textSecondary }}>
              <i className="fa-solid fa-users mr-1" />
              {submissionCount}人提交
            </span>
            {submissionCount > 0 && (
              <span className="text-[12px]" style={{ color: '#20BF6B' }}>
                {gradedCount}人已批改
              </span>
            )}
          </div>
        ) : (
          <span className="text-[12px]" style={{ color: textSecondary }}>
            截止：{homework.deadline}
          </span>
        )}

        <i className="fa-solid fa-chevron-right text-xs" style={{ color: textMuted }} />
      </div>
    </button>
  )
}

// 作业列表组件
export function HomeworkList({ homeworks, emptyText = '暂无作业' }) {
  const { textMuted } = useTheme()

  if (homeworks.length === 0) {
    return (
      <div className="text-center py-10">
        <i className="fa-solid fa-inbox text-4xl mb-3" style={{ color: textMuted }} />
        <p className="text-sm" style={{ color: textMuted }}>{emptyText}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {homeworks.map(h => (
        <HomeworkCard key={h.id} homework={h} />
      ))}
    </div>
  )
}
